import { Loader2 } from "lucide-react";
import { useEffect, useState, type FormEvent } from "react";
import { Navigate, useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AuthApiError, verify2faRequest, useAuth } from "@/lib/auth";
import {
  clearMfaSession,
  readMfaSession,
} from "@/lib/auth/mfa-session";

function messageFor(err: AuthApiError): string {
  switch (err.code) {
    case "invalid_code":
      return "That code didn't match. Try again with a fresh code from your authenticator app.";
    case "invalid_mfa_session":
      return "Your sign-in session expired. Please enter your password again.";
    case "rate_limited":
      return "Too many incorrect codes. Please wait a minute and try again.";
    case "not_enrolled":
      return "Two-factor authentication is no longer enabled for this account.";
    case "unauthorized":
      return "Sign-in session is no longer valid. Please enter your password again.";
    default:
      return err.serverMessage ?? "Couldn't verify the code. Please try again.";
  }
}

export function LoginTwoFactorPage() {
  const navigate = useNavigate();
  const { setAuthed } = useAuth();
  const [stash] = useState(() => readMfaSession());

  const [code, setCode] = useState("");
  const [trustDevice, setTrustDevice] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // If someone navigates here directly without going through /login first,
    // bounce them back. The <Navigate> below handles the initial render; this
    // covers the rare case where the stash is invalidated mid-flow (e.g.
    // another tab logged in).
  }, []);

  if (!stash) {
    return <Navigate to="/login" replace />;
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (submitting || !stash) return;
    setError(null);

    const normalized = code.replace(/\D/g, "");
    if (normalized.length !== 6) {
      setError("Enter the 6-digit code from your authenticator app.");
      return;
    }

    setSubmitting(true);
    try {
      const result = await verify2faRequest({
        mfaSession: stash.token,
        code: normalized,
        trustDevice,
      });
      clearMfaSession();
      setAuthed({ accessToken: result.accessToken, user: result.user });
      navigate(stash.next, { replace: true });
    } catch (err) {
      if (err instanceof AuthApiError) {
        setError(messageFor(err));
        if (err.code === "invalid_mfa_session" || err.code === "unauthorized") {
          clearMfaSession();
        }
      } else {
        setError("Couldn't reach the server. Check your connection and retry.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 py-10">
      <div className="w-full max-w-sm space-y-6">
        <div className="space-y-1 text-center">
          <p className="font-mono text-xs uppercase tracking-wide text-muted-foreground">
            Two-factor
          </p>
          <h1 className="text-xl font-semibold tracking-tight">
            Enter your verification code
          </h1>
        </div>

        <Card>
          <CardHeader className="space-y-1">
            <CardTitle className="text-sm normal-case tracking-normal text-foreground">
              Authenticator app
            </CardTitle>
            <CardDescription>
              Open Google Authenticator, 1Password, Authy, or your preferred
              app and enter the 6-digit code shown for Finance.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4" noValidate>
              <div className="space-y-1.5">
                <Label htmlFor="code">6-digit code</Label>
                <Input
                  id="code"
                  type="text"
                  inputMode="numeric"
                  pattern="\d{6}"
                  maxLength={6}
                  autoComplete="one-time-code"
                  autoFocus
                  required
                  value={code}
                  onChange={(e) =>
                    setCode(e.target.value.replace(/\D/g, "").slice(0, 6))
                  }
                  disabled={submitting}
                  className="font-mono text-base tracking-widest"
                  aria-describedby="code-help"
                />
                <p
                  id="code-help"
                  className="text-xs text-muted-foreground"
                >
                  The code rotates every 30 seconds.
                </p>
              </div>

              <label className="flex cursor-pointer items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  className="mt-0.5 h-4 w-4 rounded border-input"
                  checked={trustDevice}
                  onChange={(e) => setTrustDevice(e.target.checked)}
                  disabled={submitting}
                />
                <span className="space-y-0.5">
                  <span className="block text-sm text-foreground">
                    Trust this device for 7 days
                  </span>
                  <span className="block text-xs text-muted-foreground">
                    Skip the 2FA prompt on this browser for the next week.
                  </span>
                </span>
              </label>

              {error ? (
                <p
                  role="alert"
                  className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive"
                >
                  {error}
                </p>
              ) : null}

              <Button type="submit" className="w-full" disabled={submitting}>
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Verifying…
                  </>
                ) : (
                  "Verify and sign in"
                )}
              </Button>

              <button
                type="button"
                onClick={() => {
                  clearMfaSession();
                  navigate("/login", { replace: true });
                }}
                className="w-full text-xs text-muted-foreground hover:text-foreground hover:underline"
                disabled={submitting}
              >
                Use a different account
              </button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
